import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import LoginPage from "./pages/Login/LoginPage";
import MainNavbar from "./components/AssetsComponent/mainNavbar";
import PrivateRoute from "./components/AssetsComponent/PrivateRoute";
import { Toaster } from "react-hot-toast";
import NotificationPage from "./pages/Notifications/NotificationPage";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/en";
import "dayjs/locale/en";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "./store";
import "./utils/extensions.init";
import useOnlineStatus from "./hooks/useOnlineStatus";
import NoInternet from "./components/NoInternet";
import CustomerNotificationsPage from "./pages/Notifications/CustomerNotificationPage";
import GlobalSocketListener from "./components/GlobalSocketListener/GlobalSocketListener";
import TaskNotificationSidebar from "./components/TaskManagement/TaskNotificationSidebar";
import TaskNotificationCountSocket from "./components/GlobalSocketListener/TaskNotificationCountSocket";
import { getRoutes } from "./store/slices/navigationSlice";
import { NavLinkItem } from "./models/routes.model";
import {
  commonRoutes,
  navComponentMap,
} from "./components/Assets/navComponentsMap";
import LoginWithOtpPage from "./pages/Login/LoginWithOtp";

const App: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();
  const { role } = useSelector((state: RootState) => state?.auth);
  const token = useSelector((state: RootState) => state.auth.token);
  const { nav: config, loading } = useSelector(
    (state: RootState) => state.routesSlice,
  );
  useEffect(() => {
    if (token) {
      dispatch(getRoutes());
    }
  }, [dispatch, token]);

  const isOnline = useOnlineStatus();
  if (!isOnline) {
    return <NoInternet />;
  }
  const getDefaultRoute = () => {
    if (!config || Object.keys(config).length === 0) return "/";

    const mainRoutes = config.main as NavLinkItem[];

    // if "/" already exists in routes, use it
    const rootRoute = mainRoutes.find((route) => route.path === "/");
    if (rootRoute) return "/";

    // otherwise use first route
    return mainRoutes?.[0]?.path || "/";
  };
  const getRoutesForRole = () => {
    if (!config || Object.keys(config).length === 0) return null;
    const allRoutes = [
      ...(config.main as NavLinkItem[]),
      ...((config.extras as NavLinkItem[]) || []),
      ...((config.frelit as NavLinkItem[]) || []),
    ];

    return allRoutes.map((route) => {
      return (
        <Route
          key={route.path}
          path={route.path}
          element={
            <PrivateRoute>
              {route.component
                ? React.createElement(navComponentMap[route.component])
                : null}
            </PrivateRoute>
          }
        />
      );
    });
  };

  // Get special routes (notifications, etc.)
  const getNotificationRoutes = () => {
    if (!config) return null;

    return (
      <>
        {config.notifications && (
          <Route
            path={(config.notifications as NavLinkItem).path}
            element={
              <PrivateRoute>
                <NotificationPage />
              </PrivateRoute>
            }
          />
        )}
        {config.customerNotifications && (
          <Route
            path={(config.customerNotifications as NavLinkItem).path}
            element={
              <PrivateRoute>
                <CustomerNotificationsPage />
              </PrivateRoute>
            }
          />
        )}
      </>
    );
  };

  const getCommonRoutes = () => {
    return commonRoutes.map((route) => (
      <Route
        key={route.path}
        path={route.path}
        element={
          <PrivateRoute>
            {route.component ? React.createElement(route.component) : null}
          </PrivateRoute>
        }
      />
    ));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Router>
        <Toaster
          position="bottom-right"
          containerStyle={{ zIndex: 999999 }}
          toastOptions={{
            duration: 6000,
            style: { zIndex: 999999 },
          }}
        />
        <GlobalSocketListener />
        <TaskNotificationCountSocket />
        <TaskNotificationSidebar />

        {role ? (
          <PrivateRoute>
            <MainNavbar role={role} />

            <div className="flex-grow">
              {loading || !config || Object.keys(config).length === 0 ? (
                <div className="flex items-center justify-center h-screen">
                  Loading...
                </div>
              ) : (
                <Routes>
                  {getRoutesForRole()}
                  {getNotificationRoutes()}
                  {getCommonRoutes()}

                  <Route
                    path="/login"
                    element={
                      token ? (
                        <Navigate to={getDefaultRoute()} replace />
                      ) : (
                        <LoginPage />
                      )
                    }
                  />
                  <Route
                    path="/login-otp"
                    element={
                      token ? (
                        <Navigate to={getDefaultRoute()} replace />
                      ) : (
                        <LoginWithOtpPage />
                      )
                    }
                  />
                  <Route
                    path="*"
                    element={<Navigate to={getDefaultRoute()} replace />}
                  />
                </Routes>
              )}
            </div>
          </PrivateRoute>
        ) : (
          // Unauthenticated users or login page
          <Routes>
            <Route
              path="/login"
              element={
                token ? (
                  <Navigate to={getDefaultRoute()} replace />
                ) : (
                  <LoginPage />
                )
              }
            />
            <Route
              path="/login-otp"
              element={
                token ? (
                  <Navigate to={getDefaultRoute()} replace />
                ) : (
                  <LoginWithOtpPage />
                )
              }
            />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        )}
      </Router>
    </LocalizationProvider>
  );
};

export default App;
